// Port of ios/Sources/Calls/CallRoster.swift (Android `CallRoster`, decision for decision).
//
// Pure roster arithmetic for an active call: the `call_state` snapshot the server sends on
// admission, then the per-event deltas broadcast while it runs. Split from the call manager because
// this is the part that decides what the participant list SAYS, and a wrong fold here shows a muted
// person as talking — the kind of bug only a table of cases catches.
import type { CallSocketEvent } from './callsCodec';

/** One person on the call. Keyed by SESSION: the same user can join from two devices. */
export interface CallParticipant {
  sessionId: string;
  userId: string;
  muted: boolean;
  speaking: boolean;
  sharingScreen: boolean;
  /** When the hand went up (server epoch millis), 0 = down — hands are answered in that order. */
  raisedHandAt: number;
  /** The camera is on (`video_on` / the snapshot's `video`). */
  videoOn: boolean;
}

export type Roster = Record<string, CallParticipant>;

export function participant(sessionId: string, userId: string, patch: Partial<CallParticipant> = {}): CallParticipant {
  return { sessionId, userId, muted: true, speaking: false, sharingScreen: false, raisedHandAt: 0, videoOn: false, ...patch };
}

export const handRaised = (p: CallParticipant) => p.raisedHandAt > 0;

function update(roster: Roster, sessionId: string, userId: string, transform: (p: CallParticipant) => CallParticipant): Roster {
  if (!sessionId) return roster;
  const current = roster[sessionId] ?? participant(sessionId, userId);
  return { ...roster, [sessionId]: transform({ ...current }) };
}

export const CallRoster = {
  /** The authoritative snapshot: replaces everything previously believed. */
  fromCallStateJson(callJson: string): Roster {
    let call: Record<string, unknown>;
    try {
      const parsed = JSON.parse(callJson);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      call = parsed;
    } catch {
      return {};
    }
    if (!Array.isArray(call.sessions)) return {};
    const roster: Roster = {};
    for (const raw of call.sessions as unknown[]) {
      const s = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      const sessionId = typeof s.session_id === 'string' ? s.session_id : '';
      const userId = typeof s.user_id === 'string' ? s.user_id : '';
      if (!sessionId || !userId) continue;
      roster[sessionId] = participant(sessionId, userId, {
        muted: !(s.unmuted === true),
        speaking: s.voice === true,
        raisedHandAt: typeof s.raised_hand === 'number' ? s.raised_hand : 0,
        videoOn: s.video === true,
      });
    }
    let screenSession = typeof call.screen_sharing_session_id === 'string' ? call.screen_sharing_session_id : '';
    if (!screenSession) screenSession = typeof call.screen_sharing_id === 'string' ? call.screen_sharing_id : '';
    if (screenSession && roster[screenSession]) roster[screenSession] = { ...roster[screenSession], sharingScreen: true };
    return roster;
  },

  /** A delta folded in. Unknown sessions are ADDED on join and on any state event. */
  apply(roster: Roster, event: CallSocketEvent): Roster {
    switch (event.kind) {
      case 'userJoined': {
        if (!event.sessionId) return roster;
        return { ...roster, [event.sessionId]: roster[event.sessionId] ?? participant(event.sessionId, event.userId) };
      }
      case 'userLeft': {
        if (!(event.sessionId in roster)) return roster;
        const next = { ...roster };
        delete next[event.sessionId];
        return next;
      }
      case 'userMuted':
        return update(roster, event.sessionId, event.userId, (p) => ({ ...p, muted: event.muted, speaking: event.muted ? false : p.speaking }));
      case 'userSpeaking':
        return update(roster, event.sessionId, event.userId, (p) => ({ ...p, speaking: event.speaking }));
      case 'screenState': {
        if (!event.on) {
          const next: Roster = {};
          for (const [k, p] of Object.entries(roster)) next[k] = { ...p, sharingScreen: false };
          return next;
        }
        if (!event.sessionId) return roster;
        const next: Roster = {};
        for (const [k, p] of Object.entries(roster)) next[k] = { ...p, sharingScreen: p.sessionId === event.sessionId };
        if (!next[event.sessionId]) next[event.sessionId] = participant(event.sessionId, '', { sharingScreen: true });
        return next;
      }
      case 'handState':
        return update(roster, event.sessionId, event.userId, (p) => ({ ...p, raisedHandAt: event.raisedAt }));
      case 'videoState':
        return update(roster, event.sessionId, event.userId, (p) => ({ ...p, videoOn: event.on }));
      default:
        return roster;
    }
  },

  /** Raised hands, first raised first — who gets to speak next. */
  raisedHands(participants: CallParticipant[]): CallParticipant[] {
    return participants.filter(handRaised).sort((a, b) => a.raisedHandAt - b.raisedHandAt);
  },
};
